import { useSupplyManagementContext } from '@components/SupplyManagement/contexts/SupplyManagementContext'
import { Texto, Vertical } from '@gravitate-js/excalibrr'
import { isDefined } from '@utils/general'
import { Drawer, Form, Input, Select } from 'antd'
import type { FormInstance } from 'antd/es/form/Form'
import type { NamePath, StoreValue } from 'antd/lib/form/interface'
import type React from 'react'

import { EventScheduleInputs } from './components/EventScheduleInputs'
import { FormFooter } from './components/FormFooter'
import { RecurringScheduleInputs } from './components/RecurringScheduleInputs'
import { SelectInput } from './components/SelectInput'
import { OutageFieldTitles, type OutageForm } from './types'

const scheduleTypes = ['Event', 'Recurring']
const excludeInputKeys = ['reason_code', 'schedule_type']

export function EditOutageDrawer() {
  const {
    outageForm,
    drawerData,
    supplyOutageMetadata,
    isDrawerOpen,
    errorMessage,
    closeEditOutage,
    upsertOutage,
    cancelOutage,
  } = useSupplyManagementContext()

  async function handleSubmit() {
    await outageForm.validateFields()
    upsertOutage()
  }
  return (
    <Drawer
      placement='right'
      width={450}
      open={isDrawerOpen}
      styles={{
        body: styles.padding0,
      }}
      onClose={closeEditOutage}
      closable
      title={drawerData ? 'Edit Outage' : 'Create Outage'}
    >
      <Vertical>
        <Form layout='vertical' form={outageForm} style={styles.form}>
          <Vertical style={styles.formContent}>
            <SelectInput title='Reason' name='reason_code' options={supplyOutageMetadata?.reason_code} isRequired />
            <Form.Item label='Notes' name='notes'>
              <Input.TextArea
                placeholder='Notes'
                rows={4}
                maxLength={300}
                showCount={{
                  formatter: (count) => `${count.count}/300`,
                }}
              />
            </Form.Item>
            <Form.Item
              label='Schedule'
              name={['schedule_local', 'schedule_type']}
              initialValue='Event'
              required
              className='mb-3'
            >
              <Select placeholder='Select Schedule Type' options={scheduleTypes.map((r) => ({ value: r, label: r }))} />
            </Form.Item>
            <Texto className='mb-2 ml-1'>Enter all times in local timezone of the terminal.</Texto>
            <Form.Item noStyle dependencies={[['schedule_local', 'schedule_type']]} style={styles.scheduleItem}>
              {({ getFieldValue }) => (
                <ScheduleInputs
                  getFieldValue={getFieldValue}
                  prevScheduleType={drawerData?.schedule_local?.schedule_type}
                  outageForm={outageForm}
                />
              )}
            </Form.Item>
            <Texto category='h6' className='mb-3'>
              Impacted
            </Texto>

            {Object.entries(supplyOutageMetadata || {}).map(([key, value]) => {
              if (Array.isArray(value) && !excludeInputKeys.includes(key)) {
                return (
                  <SelectInput
                    key={key}
                    title={OutageFieldTitles[key]}
                    name={key}
                    options={value}
                    isRequired={key === 'terminal'}
                  />
                )
              }
              return null
            })}
          </Vertical>
          <FormFooter
            onSave={handleSubmit}
            onCancel={() => cancelOutage(drawerData?.id)}
            showCancel={isDefined(drawerData?.id) && !drawerData.canceled}
            errorMessage={errorMessage}
          />
        </Form>
      </Vertical>
    </Drawer>
  )
}

type ScheduleInputsProps = {
  getFieldValue: (name: NamePath) => StoreValue
  prevScheduleType: 'event' | 'recurring'
  outageForm: FormInstance<OutageForm>
}

function ScheduleInputs({ getFieldValue, prevScheduleType, outageForm }: ScheduleInputsProps) {
  const scheduleType = getFieldValue(['schedule_local', 'schedule_type'])
  const isRecurring = scheduleType === 'Recurring' || (scheduleType === undefined && prevScheduleType === 'recurring')
  return isRecurring ? <RecurringScheduleInputs form={outageForm} /> : <EventScheduleInputs form={outageForm} />
}

const styles = {
  padding0: {
    padding: '0',
  },
  backgroundWhite: {
    backgroundColor: 'white',
  },

  drawerTitle: {
    display: 'flex',
    flex: 1,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  formContent: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 16px 0 16px',
  },
  scheduleItem: {
    borderBottom: '1px solid var(--gray-200)',
  },
} as { [key: string]: React.CSSProperties }
